import { enabledFieldsPerModuleMap } from '@features/filters/fieldBasedFilters/constants';
import {
  type FIELD_FILTERS_ALLOWED_MODULE,
  FIELD_FILTERS_ENTITY,
  type IInitialFilter,
  OPERATOR_TYPE,
} from '@features/filters/types';
import i18n from '@features/i18n';
import { getCurrentDateStartEndTime } from '@utils/getCurrentDateStartEndTime';
import { isEnumEqual } from '@utils/isEnumEqual';

const {
  ID,
  CODE,
  NAME,
  TYPE,
  BRANDS,
  HIDDEN,
  BET_ID,
  BONUSES,
  ENABLED,
  TRIGGER,
  BRAND_ID,
  CATEGORY,
  DECISION,
  EVENT_ID,
  GIVEN_AT,
  GIVEN_TO,
  PLAYER_ID,
  GAME_NAME,
  GAME_TYPE,
  CAMPAIGN_ID,
  CAMPAIGN_IDS,
  FAILED_CHECK,
  OFFER_STATUS,
  TARGET_GROUP,
  TRIGGER_TYPE,
  CAMPAIGN_NAME,
  CAMPAIGN_TYPE,
  BET_ACTION_TYPE,
  TRIGGER_DATE_TO,
  GAME_SUB_PROVIDER,
  ACTIVATION_ACTION,
  BONUS_TEMPLATE_ID,
  PRIMARY_CRITERION,
  TRIGGER_DATE_FROM,
  CAMPAIGN_END_DATE,
  OCCURRENCE_DATE_TO,
  CAMPAIGN_START_DATE,
  OCCURRENCE_DATE_FROM,
  CAMPAIGN_EXECUTION_ID,
  SHOW_ADVANCED_FILTERS,
  SHOW_ONLY_PENDING_REVIEWS,
  CONTRIBUTION_CREATED_AT_TO,
  CONTRIBUTION_REPORT_GAME_ID,
  CONTRIBUTION_CREATED_AT_FROM,
  CONTRIBUTION_REPORT_GAME_TYPE_ID,
  CONTRIBUTION_REPORT_BET_REFERENCE,
  CONTRIBUTION_REPORT_GAME_SUB_PROVIDER_ID,
} = FIELD_FILTERS_ENTITY;

export const getFieldBasedFiltersInitialValues = (
  module: FIELD_FILTERS_ALLOWED_MODULE,
  timezone = ''
): Record<string, boolean | IInitialFilter> => {
  const initialValuesMap = {
    [SHOW_ADVANCED_FILTERS]: false,
    [ID]: { value: '', inversion: false, operator: OPERATOR_TYPE.EQUAL },
    [BRANDS]: { value: [], inversion: false, operator: OPERATOR_TYPE.IN },
    [NAME]: { value: '', inversion: false, operator: OPERATOR_TYPE.LIKE },
    [CODE]: { value: '', inversion: false, operator: OPERATOR_TYPE.LIKE },
    [TRIGGER]: { value: [], inversion: false, operator: OPERATOR_TYPE.IN },
    [ENABLED]: { value: false, inversion: true, operator: OPERATOR_TYPE.EQUAL },
    [BONUSES]: {
      value: [],
      inversion: false,
      operator: OPERATOR_TYPE.IN,
    },
    [DECISION]: {
      value: [],
      inversion: false,
      operator: OPERATOR_TYPE.IN,
    },
    [BET_ID]: {
      value: '',
      inversion: false,
      operator: OPERATOR_TYPE.EQUAL,
    },
    [GAME_NAME]: {
      value: [],
      inversion: false,
      operator: OPERATOR_TYPE.IN,
    },
    [GAME_TYPE]: {
      value: [],
      inversion: false,
      operator: OPERATOR_TYPE.IN,
    },
    [EVENT_ID]: {
      value: '',
      inversion: false,
      operator: OPERATOR_TYPE.EQUAL,
    },
    [TRIGGER_TYPE]: {
      value: [],
      inversion: false,
      operator: OPERATOR_TYPE.IN,
    },
    [PLAYER_ID]: {
      value: '',
      inversion: false,
      operator: OPERATOR_TYPE.EQUAL,
    },
    [OFFER_STATUS]: {
      value: [],
      inversion: false,
      operator: OPERATOR_TYPE.IN,
    },
    [FAILED_CHECK]: {
      value: [],
      inversion: false,
      operator: OPERATOR_TYPE.IN,
    },
    [HIDDEN]: {
      value: false,
      inversion: false,
      operator: OPERATOR_TYPE.EQUAL,
    },
    [CAMPAIGN_IDS]: {
      value: [],
      inversion: false,
      operator: OPERATOR_TYPE.IN,
    },
    [CAMPAIGN_TYPE]: {
      value: [],
      inversion: false,
      operator: OPERATOR_TYPE.IN,
    },
    [BET_ACTION_TYPE]: {
      value: [],
      inversion: false,
      operator: OPERATOR_TYPE.IN,
    },
    [CAMPAIGN_NAME]: {
      value: '',
      inversion: false,
      operator: OPERATOR_TYPE.LIKE,
    },
    [TARGET_GROUP]: {
      value: null,
      inversion: false,
      operator: OPERATOR_TYPE.EQUAL,
    },
    [PRIMARY_CRITERION]: {
      value: [],
      inversion: false,
      operator: OPERATOR_TYPE.IN,
    },
    [GAME_SUB_PROVIDER]: {
      value: [],
      inversion: false,
      operator: OPERATOR_TYPE.IN,
    },
    [ACTIVATION_ACTION]: {
      value: null,
      inversion: false,
      operator: OPERATOR_TYPE.EQUAL,
    },
    [CAMPAIGN_EXECUTION_ID]: {
      value: '',
      inversion: false,
      operator: OPERATOR_TYPE.EQUAL,
    },
    [CONTRIBUTION_REPORT_GAME_ID]: {
      value: [],
      inversion: false,
      operator: OPERATOR_TYPE.IN,
    },
    [SHOW_ONLY_PENDING_REVIEWS]: {
      value: false,
      inversion: false,
      operator: OPERATOR_TYPE.EQUAL,
    },
    [CONTRIBUTION_REPORT_GAME_TYPE_ID]: {
      value: [],
      inversion: false,
      operator: OPERATOR_TYPE.IN,
    },
    [TRIGGER_DATE_TO]: {
      value: null,
      inversion: false,
      operator: OPERATOR_TYPE.LESS_THAN_OR_EQUAL,
    },
    [CAMPAIGN_END_DATE]: {
      value: null,
      inversion: false,
      operator: OPERATOR_TYPE.LESS_THAN_OR_EQUAL,
    },
    [CONTRIBUTION_REPORT_BET_REFERENCE]: {
      value: '',
      inversion: false,
      operator: OPERATOR_TYPE.EQUAL,
    },
    [OCCURRENCE_DATE_TO]: {
      value: null,
      inversion: false,
      operator: OPERATOR_TYPE.LESS_THAN_OR_EQUAL,
    },
    [TRIGGER_DATE_FROM]: {
      value: null,
      inversion: false,
      operator: OPERATOR_TYPE.GREATER_THAN_OR_EQUAL,
    },
    [CAMPAIGN_START_DATE]: {
      value: null,
      inversion: false,
      operator: OPERATOR_TYPE.GREATER_THAN_OR_EQUAL,
    },
    [CONTRIBUTION_REPORT_GAME_SUB_PROVIDER_ID]: {
      value: [],
      inversion: false,
      operator: OPERATOR_TYPE.IN,
    },
    [OCCURRENCE_DATE_FROM]: {
      value: null,
      inversion: false,
      operator: OPERATOR_TYPE.GREATER_THAN_OR_EQUAL,
    },
    [CONTRIBUTION_CREATED_AT_TO]: {
      value: null,
      inversion: false,
      operator: OPERATOR_TYPE.LESS_THAN_OR_EQUAL,
    },
    [CONTRIBUTION_CREATED_AT_FROM]: {
      value: null,
      inversion: false,
      operator: OPERATOR_TYPE.GREATER_THAN_OR_EQUAL,
    },
    [BRAND_ID]: {
      value: [],
      inversion: false,
      operator: OPERATOR_TYPE.IN,
      fieldLabel: i18n.t('common.grid.filters.control.brand'),
    },
    [TYPE]: {
      value: [],
      inversion: false,
      operator: OPERATOR_TYPE.IN,
      fieldLabel: i18n.t('common.grid.filters.control.bonus.type'),
    },
    [CATEGORY]: {
      value: [],
      inversion: false,
      operator: OPERATOR_TYPE.IN,
      fieldLabel: i18n.t('common.grid.filters.control.category'),
    },
    [CAMPAIGN_ID]: {
      value: [],
      inversion: false,
      operator: OPERATOR_TYPE.IN,
      fieldLabel: i18n.t('common.grid.filters.control.campaign.name'),
    },
    [BONUS_TEMPLATE_ID]: {
      value: [],
      inversion: false,
      operator: OPERATOR_TYPE.IN,
      fieldLabel: i18n.t('common.grid.filters.control.bonus.template.name'),
    },
    [GIVEN_TO]: {
      inversion: false,
      operator: OPERATOR_TYPE.LESS_THAN_OR_EQUAL,
      fieldLabel: i18n.t('common.grid.filters.control.given.at.to'),
      value: timezone && getCurrentDateStartEndTime('end', timezone),
    },
    [GIVEN_AT]: {
      inversion: false,
      operator: OPERATOR_TYPE.GREATER_THAN_OR_EQUAL,
      fieldLabel: i18n.t('common.grid.filters.control.given.at.from'),
      value: timezone && getCurrentDateStartEndTime('start', timezone),
    },
  };

  return Object.fromEntries(
    enabledFieldsPerModuleMap[module].map((field) => [
      field,
      initialValuesMap[field],
    ])
  );
};

export const getFieldBasedPreviousFiltersInitialValues = (
  module: FIELD_FILTERS_ALLOWED_MODULE,
  timezone = ''
): Record<string, IInitialFilter> => {
  const filtersWithFormState = getFieldBasedFiltersInitialValues(
    module,
    timezone
  );

  return Object.fromEntries(
    Object.entries(filtersWithFormState).filter(
      ([field]) => !isEnumEqual(field, SHOW_ADVANCED_FILTERS)
    )
  ) as Record<string, IInitialFilter>;
};
